Woolman.Utils.prepareQueryParams();
Woolman.Collection = document.querySelector('[data-ajax-parent]');

/**
 * Generates search params from form data for filtering collection or search results.
 * @param {HTMLElement} formElement - Form element to turn into search params.
 * @returns
 */
const generateSearchParamsFromFormData = (formElement) => {
    const currentCheckboxes = {};

    for (const element of formElement.elements) {
        if (element.type !== 'checkbox' || !element.name) continue;
        const key = element.name;
        const value = element.value;
        if (!currentCheckboxes[key]) currentCheckboxes[key] = [];
        currentCheckboxes[key].push({value, checked: element.checked});
    }

    for (const element of formElement.elements) {
        const key = element.getAttribute('name');
        if (!key || !key.includes('filter')) continue;

        const value = element.value;
        const inputType = element.getAttribute('type');

        if (value === '') continue;

        if (inputType === 'checkbox') continue;

        if (inputType == 'number' && (element.hasAttribute('data-min') || element.hasAttribute('data-max'))) {
            const priceSlider = element.closest('price-range-slider');
            if (!priceSlider || !priceSlider.hasAttribute('data-updated')) {
                continue;
            }
        }

        if (inputType == 'number') {
            Shopify.queryParams[key] = value;
        } else {
            Shopify.queryParams[key] = value;
        }
    }

    // Read accepts legacy CSV-string from old bookmarks; new writes are always arrays.
    for (const [key, options] of Object.entries(currentCheckboxes)) {
        if (!key.startsWith('filter.')) continue;

        const stored = Shopify.queryParams[key];
        let existingValues;
        if (Array.isArray(stored)) {
            existingValues = stored.slice();
        } else if (typeof stored === 'string' && stored.length) {
            existingValues = [stored];
        } else {
            existingValues = [];
        }

        options.forEach(option => {
            const valIndex = existingValues.indexOf(option.value);
            if (option.checked) {
                if (valIndex === -1) {
                    existingValues.push(option.value);
                }
            } else {
                if (valIndex > -1) {
                    existingValues.splice(valIndex, 1);
                }
            }
        });

        if (existingValues.length > 0) {
            Shopify.queryParams[key] = existingValues;
        } else {
            delete Shopify.queryParams[key];
        }
    }

    // Empty arrays are truthy in JS, so check length explicitly for array values.
    Object.keys(Shopify.queryParams).forEach(key => {
        if (!key.startsWith('filter.')) return;
        const v = Shopify.queryParams[key];
        const isEmpty = Array.isArray(v) ? v.length === 0 : !v;
        if (isEmpty) {
            delete Shopify.queryParams[key];
        }
    });

    return Shopify.queryParams;
};

/**
 * This function fetches fresh collection page via AJAX request to collection's url.
 * @param {Array} selectors - Array of HTML element selectors to update.
 */
const getCollectionAJAX = async (selectors) => {
    Shopify.queryParams.page = 1;
    const CollectionURLSearchParams = getCollectionURLSearchParams(),
        url = location.pathname + '?' + CollectionURLSearchParams;

    if (selectors) {
        try {
            const res = await fetch(url);
            const body = await res.text();

            const parser = new DOMParser();
            const doc = parser.parseFromString(body, 'text/html');

            // Save show-more expanded state before replacing content
            const expandedShowMoreFilters = new Set();
            document.querySelectorAll('[data-show-more-btn][aria-expanded="true"]').forEach(btn => {
                const details = btn.closest('details.filter-group');
                if (!details) return;
                const summary = details.querySelector('summary span');
                if (summary) expandedShowMoreFilters.add(summary.textContent.trim());
            });

            for (const selector of selectors) {
                if (!document.querySelector(selector) || !doc.querySelector(selector)) continue;
                const targetElement = document.querySelector(selector);
                targetElement.innerHTML = doc.querySelector(selector).innerHTML;

                if (selector === '#product-grid-ajax') {
                    // Clear minHeight with a delay to match the overlay fade-out transition
                    // and prevent content jump when the new content is smaller than the old one.
                    const container = targetElement.closest('.product-grid-container');
                    if (container) {
                        setTimeout(() => {
                            container.style.minHeight = '';
                        }, 400);
                    }
                }
            }

            // Update counters and messages based on the new data from the AJAX response
            const gridInDoc = doc.querySelector('#product-grid-ajax');
            if (gridInDoc) {
                const filteredCount = parseInt(gridInDoc.dataset.filteredCount, 10);
                const context = gridInDoc.dataset.context;
                const zeroResultsMsg = document.getElementById('facets-no-results-message');
                const applyCountEl = document.getElementById('apply-count');

                let resultsStringOne = window.themeStrings.product_count_simple_one;
                let resultsStringOther = window.themeStrings.product_count_simple_other;

                if (context === 'search') {
                    resultsStringOne = window.themeStrings.results_with_count_one;
                    resultsStringOther = window.themeStrings.results_with_count_other;
                }

                if (applyCountEl) {
                    applyCountEl.innerHTML = (filteredCount === 1 ? resultsStringOne : resultsStringOther).replace('[count]', filteredCount);
                }

                if (zeroResultsMsg) {
                    zeroResultsMsg.classList.toggle('hide', filteredCount !== 0);
                }
            }

            window.history.replaceState('', '', url);
            Woolman.Collection.classList.remove('processing');
            document.querySelectorAll('.product-grid-container').forEach(c => c.classList.remove('processing'));
            document.querySelectorAll('.filter-form').forEach(f => {
                f.classList.remove('processing');
                f.style.minHeight = '';
            });

            // Restore show-more expanded state after replacing content
            if (expandedShowMoreFilters.size > 0) {
                const restoreShowMore = () => {
                    document.querySelectorAll('[data-show-more-btn]').forEach(btn => {
                        const details = btn.closest('details.filter-group');
                        if (!details) return;
                        const summary = details.querySelector('summary span');
                        if (!summary || !expandedShowMoreFilters.has(summary.textContent.trim())) return;

                        const list = btn.closest('.filter-options-list');
                        if (!list) return;

                        btn.setAttribute('aria-expanded', 'true');
                        const showMoreText = btn.querySelector('.show-more-text');
                        const showLessText = btn.querySelector('.show-less-text');
                        if (showMoreText) showMoreText.style.display = 'none';
                        if (showLessText) showLessText.style.display = 'block';
                        list.querySelectorAll('[data-show-more-item]').forEach(item => {
                            item.style.display = '';
                            item.classList.remove('is-hidden');
                        });
                    });
                };
                // Run immediately and also after a frame to handle custom element upgrades
                restoreShowMore();
                requestAnimationFrame(restoreShowMore);
            }

            // Re-attach close button listeners for dynamically replaced content
            for (const selector of selectors) {
                const el = document.querySelector(selector);
                if (!el) continue;
                el.querySelectorAll('[data-close]').forEach(closeBtn => {
                    closeBtn.addEventListener('click', (e) => {
                        e.preventDefault();
                        removeTrapFocus();
                        const parentElement = e.currentTarget.closest('[data-parent]');
                        if (parentElement) {
                            parentElement.classList.remove('is-open');
                            document.body.classList.remove('overflow-hidden');
                        }
                    });
                });
            }

        } catch (err) {
            location.search = CollectionURLSearchParams;
        }
    } else {
        location.search = CollectionURLSearchParams;
    }
};

/**
 * Returns URL for current collection. Utilizes URL parameters to save user's filters, sorting and pagination.
 * @returns
 */
const getCollectionURLSearchParams = () => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(Shopify.queryParams)) {
        if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v));
        } else {
            params.append(key, value);
        }
    }
    return params.toString();
};

class SortBySelect extends HTMLElement {
    constructor() {
        'use strict';
        super();

        this.select = this.querySelector('select');
        this.radios = this.querySelectorAll('input[type="radio"]');
        this.selectors = {
            grid: '#product-grid-ajax',
            pagination: '#pagination-ajax',
        };
        this.isSyncing = false; // Flag to prevent change events during sync

        if (Shopify.queryParams['sort_by']) {
            if (this.select) {
                this.select.value = Shopify.queryParams['sort_by'];
            } else if (this.radios.length > 0) {
                this.radios.forEach(radio => {
                    if (radio.value === Shopify.queryParams['sort_by']) {
                        radio.checked = true;
                    }
                });
            }
        }

        if (this.select) {
            this.select.addEventListener('change', this.onSelectChange.bind(this));
        } else if (this.radios.length > 0) {
            this.radios.forEach(radio => {
                radio.addEventListener('change', this.onSelectChange.bind(this));
            });
        }
    }

    connectedCallback() {
        // Initial sync is handled by the constructor setting values from query params
    }

    /**
     * Syncs the value of this sort-by element to all other sort-by instances
     * @param {string} value - The value to sync to all instances
     */
    syncToAllInstances(value) {
        if (this.isSyncing) return; // Prevent infinite loops

        this.isSyncing = true;
        const allSortByElements = document.querySelectorAll('sort-by');

        allSortByElements.forEach((sortByElement) => {
            if (sortByElement === this) return; // Skip self

            sortByElement.isSyncing = true; // Set flag on target element

            // Handle select elements
            if (sortByElement.select) {
                if (sortByElement.select.value !== value) {
                    sortByElement.select.value = value;
                }
            }
            // Handle radio buttons
            else if (sortByElement.radios.length > 0) {
                sortByElement.radios.forEach(radio => {
                    radio.checked = (radio.value === value);
                });
            }

            sortByElement.isSyncing = false; // Clear flag on target element
        });

        this.isSyncing = false;
    }

    async onSelectChange(event) {
        // Don't trigger AJAX if this change was caused by syncing
        if (this.isSyncing) {
            return;
        }

        const value = event.target.value;

        // Sync to all other sort-by instances (without triggering their change events)
        this.syncToAllInstances(value);

        // Now trigger the AJAX update
        const grid = document.querySelector(this.selectors.grid);
        const container = grid ? grid.closest('.product-grid-container') : null;
        if (container && !Woolman.Collection.classList.contains('processing')) {
            container.style.minHeight = `${container.offsetHeight}px`;
            container.classList.add('processing');
        }
        Woolman.Collection.classList.add('processing');
        Woolman.Utils.prepareQueryParams();
        Shopify.queryParams.sort_by = value;
        getCollectionAJAX(Object.values(this.selectors));
    }
}

if (customElements.get('sort-by') === undefined) {
    customElements.define('sort-by', SortBySelect);
}

class FacetsForm extends HTMLElement {
    constructor() {
        'use strict';
        super();

        this.el = this.querySelector('form');

        this.el.addEventListener('click', (event) => {
            const showMoreBtn = event.target.closest('[data-show-more-btn]');
            if (showMoreBtn) {
                this.toggleShowMore(showMoreBtn);
            }

            const clearBtn = event.target.closest('[data-clear-facets]');
            if (clearBtn) {
                event.preventDefault();
                this.resetAllFilters();
            }
        });

        this.selectors = {
            grid: '#product-grid-ajax',
            count: '#products-count-ajax',
            currentFacets: '#current-facets-ajax',
            pagination: '#pagination-ajax',
            sidebarFacets: '#sidebar-facets-wrapper',
            drawerFilterHeader: '#tdrawer-filter-header',
            facets: '#facets',
            drawerFooter: '.drawer-footer'
        };

        this.el.addEventListener('submit', this.onFormSubmit.bind(this));

        // Shared debounce timer so user can select multiple options within 500ms
        this._debounceTimer = null;
        this.el.addEventListener('change', this.onInputChange.bind(this));
    }

    async onFormSubmit(event) {
        event.preventDefault();
        const grid = document.querySelector(this.selectors.grid);
        const container = grid ? grid.closest('.product-grid-container') : null;
        if (container && !Woolman.Collection.classList.contains('processing')) {
            container.style.minHeight = `${container.offsetHeight}px`;
            container.classList.add('processing');
        }
        const filterForm = this.querySelector('.filter-form');
        if (filterForm) {
            filterForm.style.minHeight = `${filterForm.offsetHeight}px`;
            filterForm.classList.add('processing');
        }

        Woolman.Collection.classList.add('processing');
        Woolman.Utils.prepareQueryParams();
        generateSearchParamsFromFormData(this.el);

        const drawer = this.closest('[data-drawer]');
        if (drawer) {
            Woolman.ModalsAndDrawers.closeModalOrDrawer(drawer.getAttribute('id'));
        }

        getCollectionAJAX(Object.values(this.selectors));
    }

    async onInputChange(event) {
        const isDrawer = !!this.closest('[data-drawer]');
        const filterForm = this.querySelector('.filter-form');

        if (isDrawer) {
            if (filterForm) {
                filterForm.style.minHeight = `${filterForm.offsetHeight}px`;
                filterForm.classList.add('processing');
            }
            Woolman.Utils.prepareQueryParams();
            generateSearchParamsFromFormData(this.el);

            const drawerSelectors = [
                this.selectors.facets,
                this.selectors.drawerFooter,
                this.selectors.drawerFilterHeader
            ];
            getCollectionAJAX(drawerSelectors);
            return;
        }

        // Debounce: wait 1000ms so user can select multiple options at once.
        // No loader during this delay — the panel stays interactive.
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
            // Show loading state only when AJAX actually fires
            const grid = document.querySelector(this.selectors.grid);
            const container = grid ? grid.closest('.product-grid-container') : null;
            if (container && !Woolman.Collection.classList.contains('processing')) {
                container.style.minHeight = `${container.offsetHeight}px`;
                container.classList.add('processing');
            }
            if (filterForm) {
                filterForm.style.minHeight = `${filterForm.offsetHeight}px`;
                filterForm.classList.add('processing');
            }
            Woolman.Collection.classList.add('processing');

            Woolman.Utils.prepareQueryParams();
            generateSearchParamsFromFormData(this.el);
            getCollectionAJAX(Object.values(this.selectors));
        }, 1000);
    }

    resetAllFilters() {
        // Cancel any pending debounce timer first
        clearTimeout(this._debounceTimer);

        // Start fresh from URL, then clear all filter params
        Woolman.Utils.prepareQueryParams();
        Object.keys(Shopify.queryParams).forEach(key => {
            if (key.startsWith('filter.')) {
                delete Shopify.queryParams[key];
            }
        });

        // Uncheck checkboxes in current form to keep them in sync
        this.el.querySelectorAll('input[type="checkbox"]').forEach(input => {
            input.checked = false;
        });

        // Reset price sliders
        if (typeof PriceRangeSlider !== 'undefined' && PriceRangeSlider.resetAll) {
            PriceRangeSlider.resetAll();
        }
        document.querySelectorAll('price-range-slider').forEach(slider => {
            slider.removeAttribute('data-updated');
        });

        // Show loading state and trigger AJAX directly (bypassing onInputChange
        // to avoid prepareQueryParams re-reading stale filter params from URL)
        const grid = document.querySelector(this.selectors.grid);
        const container = grid ? grid.closest('.product-grid-container') : null;
        if (container) {
            container.style.minHeight = `${container.offsetHeight}px`;
            container.classList.add('processing');
        }
        const filterForm = this.querySelector('.filter-form');
        if (filterForm) {
            filterForm.style.minHeight = `${filterForm.offsetHeight}px`;
            filterForm.classList.add('processing');
        }
        Woolman.Collection.classList.add('processing');

        getCollectionAJAX(Object.values(this.selectors));
    }

    toggleShowMore(btn) {
        if (this.isAnimatingShowMore) return;

        const parent = btn.closest('.filter-options-list');
        if (!parent) return;
        const items = parent.querySelectorAll('[data-show-more-item]');
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';
        const showMoreText = btn.querySelector('.show-more-text');
        const showLessText = btn.querySelector('.show-less-text');
        const duration = 300;
        const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
        const gap = parseInt(getComputedStyle(parent).gap) || 0;

        this.isAnimatingShowMore = true;

        if (isExpanded) {
            // Hide
            btn.setAttribute('aria-expanded', 'false');
            if (showMoreText) showMoreText.style.display = 'block';
            if (showLessText) showLessText.style.display = 'none';

            items.forEach(item => {
                item.style.maxHeight = item.scrollHeight + 'px';
                item.style.opacity = '1';
                item.style.overflow = 'hidden';
                item.style.transition = `all ${duration}ms ${easing}`;

                // Force reflow
                item.offsetHeight;

                item.style.maxHeight = '0';
                item.style.opacity = '0';
                if (!parent.classList.contains('grid')) {
                    item.style.marginTop = `-${gap}px`;
                }
            });

            setTimeout(() => {
                items.forEach(item => {
                    item.style.display = 'none';
                    item.style.maxHeight = '';
                    item.style.opacity = '';
                    item.style.marginTop = '';
                    item.style.transition = '';
                    item.style.overflow = '';
                });
                this.isAnimatingShowMore = false;
            }, duration);

        } else {
            // Show
            btn.setAttribute('aria-expanded', 'true');
            if (showMoreText) showMoreText.style.display = 'none';
            if (showLessText) showLessText.style.display = 'block';

            items.forEach(item => {
                item.style.display = 'block';
                const targetHeight = item.scrollHeight;
                item.style.maxHeight = '0';
                item.style.opacity = '0';
                if (!parent.classList.contains('grid')) {
                    item.style.marginTop = `-${gap}px`;
                }
                item.style.overflow = 'hidden';
                item.style.transition = `all ${duration}ms ${easing}`;

                // Force reflow
                item.offsetHeight;

                item.style.maxHeight = targetHeight + 'px';
                item.style.opacity = '1';
                item.style.marginTop = '0';
            });

            setTimeout(() => {
                items.forEach(item => {
                    item.style.maxHeight = '';
                    item.style.opacity = '';
                    item.style.marginTop = '';
                    item.style.transition = '';
                    item.style.overflow = '';
                });
                this.isAnimatingShowMore = false;
            }, duration);
        }
    }
}

if (customElements.get('facets-form') === undefined) {
    customElements.define('facets-form', FacetsForm);
}

class CurrentFacetButton extends HTMLElement {
    constructor() {
        'use strict';
        super();

        this.selectors = {
            grid: '#product-grid-ajax',
            count: '#products-count-ajax',
            currentFacets: '#current-facets-ajax',
            pagination: '#pagination-ajax',
            sidebarFacets: '#sidebar-facets-wrapper',
            drawerFilterHeader: '#tdrawer-filter-header',
            facets: '#facets',
            drawerFooter: '.drawer-footer'
        };

        this.btn = this.querySelector('a');
        this.btn.addEventListener('click', this.removeFacet.bind(this));
    }

    async removeFacet(event) {
        event.preventDefault();
        const grid = document.querySelector(this.selectors.grid);
        const container = grid ? grid.closest('.product-grid-container') : null;
        if (container && !Woolman.Collection.classList.contains('processing')) {
            container.style.minHeight = `${container.offsetHeight}px`;
        }
        Woolman.Collection.classList.add('processing');
        if (event.currentTarget.dataset.filterParamName === 'filter.v.price') {
            // Delete price filter params
            delete Shopify.queryParams['filter.v.price.lte'];
            delete Shopify.queryParams['filter.v.price.gte'];

            // Reset all price range sliders (sidebar and drawer)
            if (typeof PriceRangeSlider !== 'undefined' && PriceRangeSlider.resetAll) {
                PriceRangeSlider.resetAll();
            }

            // Remove data-updated attribute from all sliders
            const allSliders = document.querySelectorAll('price-range-slider');
            allSliders.forEach(slider => {
                slider.removeAttribute('data-updated');
            });

            getCollectionAJAX(Object.values(this.selectors));
        } else {
            const inputToReset = document.querySelector(`.filter-form [data-key-value="${event.currentTarget.dataset.filterParamName}:${event.currentTarget.dataset.filterValue}"]`);
            inputToReset.checked = false;
            inputToReset.dispatchEvent(new Event('change'));
        }
    }
}

if (customElements.get('current-facet-button') === undefined) {
    customElements.define('current-facet-button', CurrentFacetButton);
}